import { gql, useQuery } from '@apollo/client';
import React, { useEffect } from 'react';
import authTokenUtil from '../../account/authTokenUtil';
import Toast from '../../components/Toast';
import type {
  PublicContentView_PostFragment,
  PublicContentView_TopicFragment,
  PublicContentViewQuery,
  PublicContentViewQueryVariables,
} from '../../graphql/generated/graphqlTypes';
import { UserType } from '../../graphql/generated/graphqlTypes';
import UIActions from '../../ui/UIActions';
import PageMeta from '../../utils/PageMeta';
import { useRouter } from '../../utils/router/v3Bridge';
import postPath from '../../utils/url/postPath';
import topicPath from '../../utils/url/topicPath';
import useAppSelector, { useAppDispatch } from '../redux/useAppSelector';

let hasShownNotice = false;

const POST_FRAGMENT = gql`
  fragment PublicContentView_post on PublicPostFields {
    id
    title
    ...postPath_post
  }
  ${postPath.fragments.post}
`;

const TOPIC_FRAGMENT = gql`
  fragment PublicContentView_topic on PublicTopicFields {
    id
    name
    ...topicPath_topic
  }
  ${topicPath.fragments.topic}
`;

type PublicContentViewProps = {
  className?: string;
  children: React.ReactNode;
} & (
  | {
      post: PublicContentView_PostFragment;
      topic?: never;
    }
  | {
      post?: never;
      topic: PublicContentView_TopicFragment;
    }
);

const PUBLIC_CONTENT_VIEW_QUERY = gql`
  query PublicContentView($host: String!) {
    organization(host: $host) {
      id
      name
    }
  }
`;

const PublicContentView = ({
  post,
  topic,
  className,
  children,
}: PublicContentViewProps) => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const isLoggedIn =
    useAppSelector(
      (state) =>
        !!state.session.user.id && state.session.user.type !== UserType.Visitor,
    ) || !!authTokenUtil.get();

  const { data } = useQuery<
    PublicContentViewQuery,
    PublicContentViewQueryVariables
  >(PUBLIC_CONTENT_VIEW_QUERY, { variables: { host: window.location.host } });

  const organizationName = data?.organization.name;

  const internalPath = topic ? topicPath(topic) : postPath(post);

  useEffect(() => {
    dispatch(UIActions.setExternalTopbarLoginRedirectPath(internalPath));

    return () => {
      dispatch(UIActions.setExternalTopbarLoginRedirectPath());
    };
  }, [dispatch, internalPath]);

  useEffect(() => {
    if (!hasShownNotice && isLoggedIn) {
      hasShownNotice = true;

      dispatch(
        UIActions.pushToast((toastProps: { onDismiss: () => void }) => (
          <Toast
            title={`You’re viewing a public ${topic ? 'topic' : 'post'}`}
            message={`Switch to the logged-in view to ${
              topic
                ? 'edit or reorganize the topic'
                : 'edit, comment, and collaborate'
            }
                `}
            onDismiss={toastProps.onDismiss}
            onSecondaryClick={() => {
              router.push(internalPath);
              toastProps.onDismiss();
            }}
            secondaryText="Switch to logged in"
          />
        )),
      );
    }
  }, [dispatch, internalPath, isLoggedIn, post, router, topic]);

  const name = topic ? topic.name : post.title;

  return (
    <>
      <PageMeta
        title={`${name} ${organizationName ? `- ${organizationName}` : ''}`}
      />
      <div id="content" className={className}>
        {children}
      </div>
    </>
  );
};

PublicContentView.fragments = {
  post: POST_FRAGMENT,
  topic: TOPIC_FRAGMENT,
};

export default PublicContentView;
